import React, { useEffect, useRef, useState } from 'react';
import { Button, Input } from 'reactstrap';
import { useLazyKbSuggestQuery } from 'src/rtk/features/chatbot/chatbotApi';

const MAX_SUGGESTIONS = 5;
const DEBOUNCE_DELAY = 200; // ms - reduced for faster real-time response

interface ChatInputProps {
  onSubmit: (text: string) => void;
  disabled?: boolean;
  placeholder?: string;
}

const ChatInput: React.FC<ChatInputProps> = ({
  onSubmit,
  disabled = false,
  placeholder = 'Type your question...',
}) => {
  const [value, setValue] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState<KbSuggestion[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const debounceTimer = useRef<NodeJS.Timeout>();
  const [triggerSuggest] = useLazyKbSuggestQuery();

  // Debounced suggestion fetch
  useEffect(() => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    if (value.trim().length > 2) {
      // Show loading state immediately for better UX
      setIsLoading(true);
      setShowSuggestions(true);

      debounceTimer.current = setTimeout(async () => {
        try {
          console.log('Fetching suggestions for:', value.trim());
          const result = await triggerSuggest({ query: value.trim(), limit: MAX_SUGGESTIONS }).unwrap();
          console.log('Suggestions API Response:', result);

          // Result is an array directly
          const suggestionsData: KbSuggestion[] = Array.isArray(result) ? result : [];

          console.log('Processed suggestions:', suggestionsData);
          setSuggestions(suggestionsData);
        } catch (error: any) {
          console.error('Suggestion fetch error - Full Details:', {
            status: error?.status,
            message: error?.message,
            data: error?.data,
            originalError: error?.originalError,
            error: error
          });
          setSuggestions([]);
        } finally {
          setIsLoading(false);
        }
      }, DEBOUNCE_DELAY);
    } else {
      setShowSuggestions(false);
      setSuggestions([]);
      setIsLoading(false);
    }

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [value, triggerSuggest]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = value.trim();
    if (trimmed && !disabled) {
      onSubmit(trimmed);
      setValue('');
      setShowSuggestions(false);
    }
  };

  const handleSuggestionClick = (suggestion: string) => {
    onSubmit(suggestion);
    setValue('');
    setShowSuggestions(false);
  };

  return (
    <div className="chatbot-input-wrapper">
      <form onSubmit={handleSubmit} className="chatbot-input-form d-flex gap-2 p-2 align-items-end">
        <Input
          type="textarea"
          rows={1}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSubmit(e);
            }
          }}
          onBlur={() => setTimeout(() => setShowSuggestions(false), 250)}
          placeholder={placeholder}
          disabled={disabled}
          className="chatbot-input flex-grow-1"
        />
        <Button type="submit" color="primary" disabled={disabled || !value.trim()} className="chatbot-send-btn">
          Send
        </Button>
      </form>

      {/* Suggestions Dropdown */}
      {showSuggestions && (
        <div className="chatbot-suggestions">
          {isLoading && (
            <div className="chatbot-suggestion-item" style={{ cursor: 'default', justifyContent: 'center' }}>
              <span className="small text-muted">
                <i className="fa fa-spinner fa-spin me-2" />
                Loading suggestions...
              </span>
            </div>
          )}
          {!isLoading && suggestions.length > 0 && suggestions.map((suggestion) => (
            <button
              key={suggestion.id}
              type="button"
              className="chatbot-suggestion-item"
              onClick={() => handleSuggestionClick(suggestion.question)}
              onMouseDown={(e) => e.preventDefault()}
            >
              <i className="fa fa-lightbulb-o me-2" />
              {suggestion.question}
            </button>
          ))}
          {!isLoading && suggestions.length === 0 && (
            <div className="chatbot-suggestion-item" style={{ cursor: 'default', justifyContent: 'center' }}>
              <span className="small text-muted">No suggestions found</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ChatInput;
